import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, X, Package, Tag } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../layouts/AdminLayout';
import { productsApi, categoriesApi } from '../../services/api';
import { formatCurrency, formatApiError, Spinner } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-800 font-manrope">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"><X size={18} className="text-slate-500" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

//const UNITS = ['kg', 'pcs', 'm2', 'meter', 'set', 'buah'];
const UNITS = [
  'pcs', 'unit', 'kg', 'set', 'buah', 'paket', 'jasa', 'item',
  'jam', 'hari', 'bulan', 'sesi', 'malam',
  'meter', 'm2', 'lembar', 'pasang', 'roll', 'titik', 'orang', 'pax',
];

export default function ProductsPage() {
  const { lang, t } = useLanguage();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('products');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [prodSearch, setProdSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [prodPage, setProdPage] = useState(1);
  const PAGE_SIZE = 20;

  const { data: productData, isLoading: loadProd } = useQuery({
    queryKey: ['products', prodSearch, catFilter, prodPage],
    queryFn: () => productsApi.list({ page: prodPage, limit: PAGE_SIZE, search: prodSearch, category_id: catFilter }).then(r => r.data),
  });
  const { data: categories = [] } = useQuery({ queryKey: ['categories'], queryFn: () => categoriesApi.list().then(r => r.data) });
  const products = productData?.items || [];

  const catMap = categories.reduce((acc, c) => { acc[c.id] = c.name; return acc; }, {});

  const totalProducts = productData?.total || 0;
  const totalProdPages = Math.ceil(totalProducts / PAGE_SIZE);
  const pagedProducts = products;

const openCreate = (type) => { 
  setModal({ type, mode: 'create' }); 
  setForm(type === 'product' ? { name: '', category_id: '', unit: 'pcs', price: '', cost_price: '', product_type: 'jasa', stock: 0, minimum_stock: 0 } : { name: '', description: '' }); 
};

  const openEdit = (type, item) => { setModal({ type, mode: 'edit', id: item.id }); setForm(item); };

  const prodMutation = useMutation({
    mutationFn: d => modal?.mode === 'create' ? productsApi.create(d) : productsApi.update(modal.id, d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }); setModal(null); toast.success(t('Produk disimpan', 'Product saved')); },
    onError: err => toast.error(formatApiError(err)),
  });

  const catMutation = useMutation({
    mutationFn: d => modal?.mode === 'create' ? categoriesApi.create(d) : categoriesApi.update(modal.id, d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['categories'] }); setModal(null); toast.success(t('Kategori disimpan', 'Category saved')); },
    onError: err => toast.error(formatApiError(err)),
  });

  const deleteProd = async (id) => { if (!window.confirm(t('Hapus produk?', 'Delete product?'))) return; await productsApi.remove(id); queryClient.invalidateQueries({ queryKey: ['products'] }); toast.success(t('Dihapus', 'Deleted')); };
  const deleteCat = async (id) => { if (!window.confirm(t('Hapus kategori?', 'Delete category?'))) return; await categoriesApi.remove(id); queryClient.invalidateQueries({ queryKey: ['categories'] }); toast.success(t('Dihapus', 'Deleted')); };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const saveProduct = () => {
    const payload = { ...form };
    if (modal?.mode === 'edit') delete payload.stock;
    prodMutation.mutate(payload);
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Produk & Layanan', 'Products & Services')}</h1>
          <button onClick={() => openCreate(activeTab === 'products' ? 'product' : 'category')}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
            <Plus size={18} />{activeTab === 'products' ? t('Tambah Produk', 'Add Product') : t('Tambah Kategori', 'Add Category')}
          </button>
        </div>

        <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
          {[{ id: 'products', icon: Package, label: t('Produk', 'Products') }, { id: 'categories', icon: Tag, label: t('Kategori', 'Categories') }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              <tab.icon size={16} />{tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'products' ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
            {/* Search + Filter bar */}
            <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-50">
              <input value={prodSearch} onChange={e => { setProdSearch(e.target.value); setProdPage(1); }} data-testid="product-search-input"
                placeholder={t('Cari produk...', 'Search products...')}
                className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44]" />
              <select value={catFilter} onChange={e => { setCatFilter(e.target.value); setProdPage(1); }} data-testid="product-category-filter"
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44] cursor-pointer">
                <option value="">{t('Semua Kategori', 'All Categories')}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-50">
                  {[t('Produk', 'Product'), t('Kategori', 'Category'), 'Tipe', 'Stok', 'Unit', t('Harga Jual', 'Price'), 'HPP', ''].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadProd ? [1,2,3].map(i => <tr key={i}>{[1,2,3,4,5,6,7,8].map(j => <td key={j} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>) :
                  pagedProducts.length === 0 ? <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-400 text-sm">{t('Belum ada produk', 'No products found')}</td></tr> :
                  pagedProducts.map(p => (
                    <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                      <td className="px-5 py-3.5"><p className="font-medium text-slate-800 text-sm">{p.name}</p></td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{catMap[p.category_id] || '-'}</td>
                      <td className="px-5 py-3.5"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${p.product_type === 'barang' ? 'bg-sky-50 text-sky-700' : 'bg-violet-50 text-violet-700'}`}>{p.product_type === 'barang' ? 'Barang' : 'Jasa'}</span></td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{p.product_type === 'barang' ? <span className={(p.stock || 0) <= (p.minimum_stock || 0) ? 'font-bold text-rose-600' : ''}>{p.stock || 0}</span> : '—'}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{p.unit}</td>
                      <td className="px-5 py-3.5 text-sm font-semibold text-slate-800">{formatCurrency(p.price)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{formatCurrency(p.cost_price)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => openEdit('product', p)} className="p-2 text-slate-400 hover:text-[#0f6c44] hover:bg-emerald-50 rounded-lg transition-colors"><Edit2 size={15} /></button>
                          <button onClick={() => deleteProd(p.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {totalProdPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-slate-50">
                <p className="text-sm text-slate-500">{totalProducts} {t('produk', 'products')} · {t('halaman', 'page')} {prodPage}/{totalProdPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setProdPage(p => Math.max(1, p - 1))} disabled={prodPage === 1} data-testid="products-previous-page-button"
                    className="px-3 py-1.5 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 disabled:opacity-40">
                    {t('Sebelumnya', 'Prev')}
                  </button>
                  <button onClick={() => setProdPage(p => Math.min(totalProdPages, p + 1))} disabled={prodPage >= totalProdPages} data-testid="products-next-page-button"
                    className="px-3 py-1.5 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 disabled:opacity-40">
                    {t('Berikutnya', 'Next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map(c => (
              <div key={c.id} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex items-start justify-between">
                <div>
                  <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center mb-3">
                    <Tag size={16} className="text-[#0f6c44]" />
                  </div>
                  <p className="font-semibold text-slate-800">{c.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{c.description}</p>
                  <p className="text-xs text-slate-400 mt-2">{products.filter(p => p.category_id === c.id).length} {t('produk', 'products')}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit('category', c)} className="p-2 text-slate-400 hover:text-[#0f6c44] hover:bg-emerald-50 rounded-lg transition-colors"><Edit2 size={15} /></button>
                  <button onClick={() => deleteCat(c.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal?.type === 'product' && (
        <Modal title={modal.mode === 'create' ? t('Tambah Produk', 'Add Product') : t('Edit Produk', 'Edit Product')} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{t('Nama Produk', 'Product Name')} *</label>
              <input value={form.name || ''} onChange={set('name')} required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{t('Kategori', 'Category')}</label>
              <select value={form.category_id || ''} onChange={set('category_id')} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none">
                <option value="">- {t('Tanpa kategori', 'No category')} -</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Unit</label>
                <select 
  value={form.unit || 'pcs'} 
  onChange={set('unit')} 
  className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none"
>
  {Array.from(new Set([...UNITS, ...(form.unit ? [form.unit] : [])])).map(u => (
    <option key={u} value={u}>{u}</option>
  ))}
</select>

</div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{t('Harga Jual', 'Price')}</label>
                <input type="number" value={form.price || ''} onChange={set('price')} className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">HPP</label>
                <input type="number" value={form.cost_price || ''} onChange={set('cost_price')} className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" /></div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Tipe Produk</label>
              <div className="grid grid-cols-2 gap-2">
                {[{ value: 'jasa', label: 'Jasa' }, { value: 'barang', label: 'Barang' }].map(option => <button type="button" key={option.value} onClick={() => setForm(current => ({ ...current, product_type: option.value }))} data-testid={`product-type-${option.value}`} className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${form.product_type === option.value ? 'border-[#0f6c44] bg-emerald-50 text-[#0f6c44]' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>{option.label}</button>)}
              </div>
            </div>
            {form.product_type === 'barang' && <div className="grid grid-cols-2 gap-3 rounded-xl border border-sky-100 bg-sky-50/50 p-3">{modal.mode === 'create' ? <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Stok Awal</label><input type="number" min="0" value={form.stock ?? 0} onChange={set('stock')} data-testid="product-stock-input" className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0f6c44]" /></div> : <div><p className="text-sm font-medium text-slate-700">Stok Saat Ini</p><p className="mt-2 text-lg font-bold text-slate-800">{form.stock ?? 0} {form.unit}</p><p className="mt-1 text-xs text-slate-500">Ubah stok melalui menu Inventory.</p></div>}<div><label className="mb-1.5 block text-sm font-medium text-slate-700">Stok Minimum</label><input type="number" min="0" value={form.minimum_stock ?? 0} onChange={set('minimum_stock')} data-testid="product-minimum-stock-input" className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0f6c44]" /></div></div>}
            <button onClick={saveProduct} disabled={prodMutation.isPending || !form.name} data-testid="save-product-button"
              className="w-full h-11 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] transition-all flex items-center justify-center disabled:opacity-70">
              {prodMutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : t('Simpan', 'Save')}
            </button>
          </div>
        </Modal>
      )}

      {modal?.type === 'category' && (
        <Modal title={modal.mode === 'create' ? t('Tambah Kategori', 'Add Category') : t('Edit Kategori', 'Edit Category')} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{t('Nama Kategori', 'Category Name')} *</label>
              <input value={form.name || ''} onChange={set('name')} required autoFocus className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{t('Deskripsi', 'Description')}</label>
              <input value={form.description || ''} onChange={set('description')} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" /></div>
            <button onClick={() => catMutation.mutate(form)} disabled={catMutation.isPending || !form.name}
              className="w-full h-11 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] transition-all flex items-center justify-center disabled:opacity-70">
              {catMutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : t('Simpan', 'Save')}
            </button>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}
